import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, ShieldCheck, FileText, ClipboardCheck, LifeBuoy,
  Send, UserCheck, Factory, Warehouse, Building2, Users, Target, Globe2, Lock, Layers,
  Plus, Minus,
} from "lucide-react";
import { useSEO, ORG_JSONLD } from "@/lib/seo";
import { trackCTA } from "@/lib/analytics";
import CTASection from "@/components/CTASection";
import BookCallButton from "@/components/BookCallButton";

const HERO_IMG =
  "https://images.unsplash.com/photo-1516937941344-00b4e0337589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGNoZW1pY2FsJTIwcGxhbnQlMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc4NjQyNDA1NHww&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const WHAT_WE_DO = [
  { num: "01", icon: FileText, title: "Prepare", text: "We organize your chemical inventory, SDS and facility information." },
  { num: "02", icon: ClipboardCheck, title: "Review", text: "Our team reviews the information needed for reporting." },
  { num: "03", icon: LifeBuoy, title: "Support", text: "We provide the reporting package and support the applicable submission process." },
];

const HOW_IT_WORKS = [
  { icon: Send, title: "Send Your Information", text: "Chemical inventory, SDS and facility details." },
  { icon: FileText, title: "We Prepare", text: "Our team organizes and prepares the reporting information." },
  { icon: UserCheck, title: "You Review", text: "You review and confirm the information." },
  { icon: LifeBuoy, title: "Submission Support", text: "We support the applicable reporting / submission workflow." },
];

const WHO_WE_HELP = [
  { icon: Factory, label: "Manufacturers" },
  { icon: Warehouse, label: "Warehouses" },
  { icon: Building2, label: "Industrial Facilities" },
  { icon: Users, label: "Environmental Consultants" },
];

const WHY = [
  { icon: Target, title: "Specialized", text: "Focused on Tier II / EPCRA reporting support." },
  { icon: Globe2, title: "Remote", text: "Get additional EHS reporting capacity without hiring another employee." },
  { icon: Lock, title: "Confidential", text: "Facility and chemical information is handled confidentially." },
  { icon: Layers, title: "Scalable", text: "Support for individual facilities or multiple locations." },
];

const FAQS = [
  { q: "What is Tier II reporting?", a: "Tier II reporting is the annual reporting of hazardous chemicals stored at a facility to the appropriate authorities. Requirements vary by state and facility." },
  { q: "Who needs to file a Tier II report?", a: "Facilities that store hazardous chemicals above certain thresholds. Whether a report is required depends on your chemicals, quantities and state — we can help you figure that out." },
  { q: "Can you help with multiple facilities?", a: "Yes. We support single facilities and multi-location portfolios using a standardized, repeatable workflow." },
  { q: "What information do you need from us?", a: "Your chemical inventory, Safety Data Sheets (SDS) and basic facility details. Send what you have and we'll guide you on the rest." },
  { q: "How does the process work?", a: "Send us your information, our team prepares the reporting information, you review and confirm it, and we support the applicable submission process." },
  { q: "Do you work with environmental consulting firms?", a: "Yes. Many firms use Wasserlab as their remote Tier II reporting support team so they can take on more clients without adding staff." },
  { q: "How do I get started?", a: "Request a free Tier II assessment. Tell us a little about your facility and we'll follow up to discuss how we can help." },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function AssessmentButton({ location, label = "Get a Free Tier II Assessment", testid }) {
  return (
    <Link
      to="/free-tier-ii-assessment"
      onClick={() => trackCTA("Free Assessment", location)}
      className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-7 py-4 text-base font-bold text-navy-900 transition-transform hover:-translate-y-0.5 hover:bg-amber-400"
      data-testid={testid}
    >
      {label}
      <ArrowRight className="h-5 w-5" aria-hidden="true" />
    </Link>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  useSEO({
    title: "Tier II Reporting Help for U.S. Facilities & Consultants",
    description:
      "Need help with Tier II reporting? Wasserlab prepares Tier II / EPCRA reporting information for U.S. facilities and consulting firms. Send us your facility and chemical information — get a free assessment.",
    path: "/",
    jsonLd: [ORG_JSONLD, FAQ_JSONLD],
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy-900 noise-overlay" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Industrial chemical facility" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-navy-900/70" />
          <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-amber-400">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Wasserlab — EHS Remote
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
              Need Help With Tier II Reporting?
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-200">
              We prepare Tier II / EPCRA reporting information for U.S. facilities. Send us your facility and chemical
              information — our team helps organize, prepare and review your reporting information.
            </motion.p>
            <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="mt-9 flex flex-col sm:flex-row gap-4">
              <AssessmentButton location="hero" testid="hero-cta-assessment" />
              <BookCallButton location="hero" />
            </motion.div>
            <motion.p initial="hidden" animate="show" custom={4} variants={fade} className="mt-6 text-sm font-medium text-slate-300">
              For manufacturers, warehouses, industrial facilities and environmental consulting firms.
            </motion.p>
          </div>
        </div>
      </section>

      {/* WHAT IS TIER II? */}
      <section className="bg-white py-16 lg:py-20" data-testid="what-is-tier-ii">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">The Simple Version</p>
          <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy-900">What is Tier II Reporting?</h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Tier II reporting is the annual reporting of hazardous chemicals stored at a facility to the appropriate
            authorities.
          </p>
          <p className="mt-4 text-lg font-semibold text-navy-900">Need help preparing yours? We can help.</p>
          <div className="mt-7 flex justify-center">
            <AssessmentButton location="what-is-tier-ii" label="Get Started" testid="tier-ii-cta-get-started" />
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-slate-50 py-20 lg:py-24" data-testid="home-what-we-do">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">What We Do</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy-900">Prepare. Review. Support.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHAT_WE_DO.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.num}
                  initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }} custom={i} variants={fade}
                  className="rounded-2xl border border-slate-200 bg-white p-8"
                  data-testid={`what-we-do-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy-900 text-amber-500">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="font-heading text-3xl font-extrabold text-slate-200">{c.num}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-bold text-navy-900">{c.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-slate-600">{c.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-20 lg:py-24" data-testid="home-how-it-works">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">How It Works</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy-900">Four simple steps</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="relative rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid={`how-step-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-white font-bold">{i + 1}</span>
                  <Icon className="mt-5 h-6 w-6 text-navy-900" aria-hidden="true" />
                  <h3 className="mt-3 font-heading text-base font-bold text-navy-900">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-slate-500">
            Requirements vary by state and facility. Wasserlab provides reporting and compliance support; final
            determinations and submissions remain the client's responsibility.
          </p>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="bg-navy-900 py-20 lg:py-24 noise-overlay" data-testid="home-who-we-help">
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Who We Help</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Built for the people who store reportable chemicals — and the firms that serve them
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {WHO_WE_HELP.map((w, i) => {
              const Icon = w.icon;
              return (
                <div key={i} className="flex flex-col items-start gap-4 rounded-xl border border-white/10 bg-white/5 p-6" data-testid={`who-we-help-${i}`}>
                  <Icon className="h-7 w-7 text-emerald-400" aria-hidden="true" />
                  <span className="font-heading text-base font-semibold text-white">{w.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOR CONSULTANTS */}
      <section className="bg-white py-20 lg:py-24" data-testid="home-consultants">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">For Environmental Consultants</p>
              <h2 className="mt-3 font-heading text-2xl lg:text-3xl font-bold tracking-tight text-navy-900">Have Too Many Tier II Reports to Handle?</h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                Use Wasserlab as your remote Tier II reporting support team. You manage the client relationship — we
                support the reporting workload, delivered remotely and confidentially.
              </p>
            </div>
            <Link
              to="/for-consultants"
              onClick={() => trackCTA("Partner With Us", "home-consultants")}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-transform hover:-translate-y-0.5 shrink-0"
              data-testid="home-consultants-cta"
            >
              Partner With Us <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY WASSERLAB */}
      <section className="bg-slate-50 py-20 lg:py-24" data-testid="why-wasserlab">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Why Wasserlab?</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy-900">A specialized remote reporting partner</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {WHY.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="border-l-2 border-emerald-500 pl-5" data-testid={`why-point-${i}`}>
                  <Icon className="h-6 w-6 text-navy-900" aria-hidden="true" />
                  <h3 className="mt-3 font-heading text-base font-bold text-navy-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24" data-testid="home-faq">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">FAQ</p>
            <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-navy-900">Common questions</h2>
          </div>
          <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 py-5 text-left"
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open}
                  >
                    <span className="font-heading text-base font-bold text-navy-900">{f.q}</span>
                    {open ? <Minus className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" /> : <Plus className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />}
                  </button>
                  {open && <p className="pb-5 text-base leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{f.a}</p>}
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex flex-col items-center gap-3 text-center">
            <p className="text-base font-semibold text-navy-900">Need help? Get a free assessment.</p>
            <AssessmentButton location="faq" testid="faq-cta-assessment" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <CTASection heading="Need Help With a Tier II Report?" sub="Send us your facility and chemical information — we'll review it and follow up. No obligation." location="home-footer" />
    </>
  );
}
